import {
    Box, Typography, Divider, Select, MenuItem, FormControl, InputLabel, Button, Chip,
    Slider, Checkbox, ListItemText, Table, TableBody, TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
    TableCell, TableContainer, TableHead, TableRow, IconButton, ListSubheader, FormControlLabel, FormGroup, Switch, AccordionDetails, Accordion, styled, AccordionSummary
} from '@mui/material';
import MuiAccordionSummary, {
    AccordionSummaryProps,
  } from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState, useEffect } from 'react';
import SearchableDropdown from "../components/SearchableDropdown";
import PlotOptionsModal from './PlotOptionsModal';
import CategoryIcon from '@mui/icons-material/Category'; // An icon for categorical
import DataArrayIcon from '@mui/icons-material/DataArray'; // An icon for data
import DataObjectIcon from '@mui/icons-material/DataObject';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DataProcessingStep from './DataProcessingStep';
import CustomFilterStep from './CustomFilter';
import FilterPill from './FilterPill';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CustomFilterModal from './CustomFilterModal';
import { ExpandMore } from '@mui/icons-material';




interface ColorPillProps {
    count: number;
    color: string;
}

const ColorPill: React.FC<ColorPillProps> = ({ count, color }) => (
    <Chip 
        label={count} 
        style={{backgroundColor: color, margin: '0.2rem', width: '49px', height: '25px'}} 
    />
);

function transformToAxisItem(valueObj: ExtendedOptionValue): AxisItem {
    return {
        versionType: valueObj.id || null,
        name: valueObj.label,
        method: 'data', // or other appropriate default values
        values: [],
    };
}

const CustomAccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
      {...props}
    />
  ))(({ theme }) => ({
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, .05)'
        : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    // '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    //   transform: 'rotate(90deg)',
    // },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        transform: 'rotate(-90deg)',
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(0deg)',
    },
}));

const PlotControls: React.FC<PlotControlsProps> = ({
    controlGroups,
    tableItems,
    plotConfig,
    plotName,
    groupedSamples,
    showGrouped,
    onGroupSamples,
    updateFilters,
    setDataPipeline,
    handlePlotNameChange,
    openPlotOptionsModal,
    updatePlotConfig,
    savePlot,
    disableSave,
    saveSuccess,
    availableColumns,
    categoricalColumns, 
  }) => {

    const [modalOpen, setModalOpen] = useState(false);
    const sampleSelections = ['Sample Title', 'Sample Group', 'Hyperlink', 'Treatment_and_Dose', 'Timepoint', 'channel_id'];
    
    const [currentControl, setCurrentControl] = useState<Control | null>(null);
    const [dialogSelectedColumn, setDialogSelectedColumn] = useState<SearchableDropdownOption[]>([]);
    const [dialogDataPipelines, setDialogDataPipelines] = useState<number[]>([]);
    const [dialogGroupBy, setDialogGroupBy] = useState<SearchableDropdownOption[]>([]);
    const [dialogAggregationMethod, setDialogAggregationMethod] = useState<string>('mean');
    const [dialogNormalizeBy, setDialogNormalizeBy] = useState<SearchableDropdownOption[]>([]);
    const [dialogNormalizeUnits, setDialogNormalizeUnits] = useState<string>('fold_change');
    const [dialogRelativeUnits, setDialogRelativeUnits] = useState<string>('fold_change');
    const [dialogReferences, setDialogReferences] = useState<Reference[]>([]);
    const [dialogProcessingMode, setDialogProcessingMode] = useState<'absolute' | 'relative'>('absolute')
    const [dialogHideReference, setDialogHideReference] = useState<boolean>(false)
    const [dialogGroupByRelative, setDialogGroupByRelative] = useState<SearchableDropdownOption[]>([])
    // dialog reference information for and value for normalize by reference
    const [dialogNormReferences, setDialogNormReferences] = useState<Reference[]>([])
    const [dialogNormHideReference, setDialogNormHideReference] = useState<boolean>(false)
    const [dialogNormGroupByRelative, setDialogNormGroupByRelative] = useState<SearchableDropdownOption[]>([])
    const [dialogNormRelativeUnits, setDialogNormRelativeUnits] = useState<string>('fold_change')
    const [dialogPathwayMode, setDialogPathwayMode] = useState<boolean>(false)
    const [dialogPathwayScore, setDialogPathwayScore] = useState<string>('mean')
    // const [dialogReferenceValue, setDialogReferenceValue] = useState<SearchableDropdownOption[]>([])
    // const [dialogReferenceColumn, setDialogReferenceColumn] = useState<SearchableDropdownOption[]>([])
    const [treatAsCategory, setTreatAsCategory] = useState(false);
    const [currentItem, setCurrentItem] = useState<AxisItem | null>(null);
    const [savePlotModalOpen, setSavePlotModalOpen] = useState(false);
    const [filterValue, setFilterValue] = useState<FilterOption[]>([]);
    const [customFilterModalOpen, setCustomFilterModalOpen] = useState(false);
    const [dataSelectExpand, setDataSelectExpand] = useState<boolean>(true);

    const [error, setError] = useState<string>('');


    const {
        selectedItems = {},
        setItem = () => {},
        addItem = () => {},
        removeItem = () => {},
        clearItems = () => {}
    } = tableItems || {};

    const handleFilterValue = (value: FilterOption[]) => {
        setFilterValue(value);
    };   

    const handleFilterItem = (control: Control, item: AxisItem) => {
        setCurrentControl(control);
        setFilterValue(item.filterItems || []);
        setCurrentItem(item);
        setCustomFilterModalOpen(true);
    }
    
    const handleEditItem = (control: Control, item: AxisItem) => {
        setCurrentControl(control);
        setCurrentItem(item);

        setDialogGroupBy(item.groupBy ? item.groupBy.map(item => ({label: item, value: item})) : []);
        setDialogAggregationMethod(item.aggregationMethod || 'mean');
        setDialogNormalizeBy(item.normalizeBy ? [{label: item.normalizeBy.split(/\|([\s\S]*)/)[1], value: item.normalizeBy}] : [])
        setDialogReferences(item.references ? item.references : [])
        setDialogNormalizeUnits(item.normalizeUnits ? item.normalizeUnits : 'fold_change')
        setDialogRelativeUnits(item.relativeUnits ? item.relativeUnits : 'fold_change')
        setDialogDataPipelines(item.pipelines || [])
        setDialogProcessingMode(item.processingMode ? item.processingMode : 'absolute')
        setDialogHideReference(item.hideReference ? item.hideReference : false)
        setDialogGroupByRelative(item.groupByRelative ? item.groupByRelative.map(item => ({label: item, value: item})) : [{label:'dummy', value:'dummy'}])
        setDialogNormReferences(item.normReferences ? item.normReferences : [])
        setDialogNormHideReference(item.normHideReference ? item.normHideReference : false)
        setDialogNormGroupByRelative(item.groupByNormRelative ? item.groupByNormRelative.map(item => ({label: item, value: item})) : []) 
        setDialogNormRelativeUnits(item.normRelativeUnits ? item.normRelativeUnits : 'fold_change')
        setDialogPathwayMode(item.pathwayMode ? item.pathwayMode : false)
        setDialogPathwayScore(item.pathwayScore ? item.pathwayScore : 'mean') 


        // Set dialog states with the item's existing values
        setDialogSelectedColumn(item.values.map(value => ({label: value, value: value})) || []);
        setTreatAsCategory(item.method === 'categorical');
        setModalOpen(true);
    };


    const handleCategoryToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTreatAsCategory(event.target.checked);
    };

    const handleSelectedColumn = (newSelectedColumn: string[]) => {
        updatePlotConfig((prevConfig: any) => ({
            ...prevConfig,
            selectedColumn: newSelectedColumn,
        }));
    }

    useEffect(()=> {
        const pipelines: number[] = []
        if (dialogSelectedColumn.length > 0){
            dialogSelectedColumn.forEach((value: SearchableDropdownOption) => {
                if (value.pipeline){
                    pipelines.push(value.pipeline)
                }
            })
        }
        setDialogDataPipelines(pipelines)
    }, [dialogSelectedColumn])

    const handlePlotConfigChange = (key: string, value: any) => {
    
        // Construct new config based on existing config and the updated key-value pair
        const newConfig = {
            ...plotConfig,
            [key]: value,
        };

        // Call updatePlotConfig with the new configuration
        updatePlotConfig(newConfig);
    };
    
    function getColorForValue(value: number): string {
        // Define color codes - more muted shades
        const blue = [75, 112, 216];
        const yellow = [255, 224, 130];
        const red = [220, 90, 90];
    
        let startColor, endColor, t;
    
        if (value < 10) {
            return `rgb(${blue[0]}, ${blue[1]}, ${blue[2]})`; // directly return muted blue
        } else if (value >= 300) {
            return `rgb(${red[0]}, ${red[1]}, ${red[2]})`; // directly return muted red
        } else if (value < 155) {
            startColor = blue;
            endColor = yellow;
            // Normalizing the value to the range [0,1] for interpolation
            t = (value - 10) / 145; 
        } else {
            startColor = yellow;
            endColor = red;
            t = (value - 155) / 145; 
        }
    
        // Calculate interpolated color value
        const r = (1 - t) * startColor[0] + t * endColor[0];
        const g = (1 - t) * startColor[1] + t * endColor[1];
        const b = (1 - t) * startColor[2] + t * endColor[2];
    
        return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
    }
    
    const getSubOptionsCount = (control: Control, itemName: string): number => {
        const optionObj = control.options?.find(opt => 'options' in opt && opt.label === itemName);
        return optionObj && 'options' in optionObj ? optionObj.options.length : 0;
    }

    const getSubOptions = (control: Control, itemName: string): OptionValue[] | undefined => {
        const option = control.options?.find(opt => 'options' in opt && opt.label === itemName);
        return option && 'options' in option ? option.options : undefined;
    }    

    const canAddRow = (label: string) => {
        const items = selectedItems[label] || [];
        return items.length === 0 || !!items[items.length - 1]?.name;
    };

    const handleFilterDialogSave = () => {
        if (currentControl && currentItem) {
            const itemIndex = selectedItems[currentControl.label]?.findIndex(i => i.name === currentItem.name);

            if (itemIndex !== undefined && itemIndex !== -1) {
                const updatedItem: AxisItem = {
                    ...currentItem,
                    filterItems: filterValue,
                };
                setItem(currentControl.label, itemIndex, updatedItem);
            }
        }
    }

    const handleDialogSave = () => {
        if (currentControl && currentItem) {
            const itemIndex = selectedItems[currentControl.label]?.findIndex(i => i.name === currentItem.name);

            let method = 'data'
            if ( sampleSelections.includes(currentItem.name)) {
                method = 'index'
            }
            if (treatAsCategory) {
                method = 'categorical'
            }

            if (itemIndex !== undefined && itemIndex !== -1) {
                const updatedItem: AxisItem = {
                    ...currentItem,
                    values: dialogSelectedColumn.map(option => option.value),
                    method: method,
                    pipelines: dialogDataPipelines,
                    groupBy: dialogGroupBy.map(option => option.value),
                    aggregationMethod: dialogAggregationMethod,
                    normalizeBy: !dialogNormalizeBy.length ? undefined: dialogNormalizeBy.map(option => option.value)[0],
                    normalizeUnits: dialogNormalizeUnits || undefined,
                    relativeUnits: dialogRelativeUnits || undefined,
                    // referenceColumn: !dialogReferenceColumn.length ? undefined: dialogReferenceColumn.map(option => option.value)[0],
                    // referenceValue: !dialogReferenceValue.length ? undefined: dialogReferenceValue.map(option => option.value)[0],
                    references: dialogReferences,
                    processingMode: dialogProcessingMode,
                    hideReference: dialogHideReference, 
                    groupByRelative: (dialogGroupByRelative.length >= 1 && dialogGroupByRelative[0].value === 'dummy') ? undefined : dialogGroupByRelative.map(option => option.value),
                    normReferences: dialogNormReferences,
                    normHideReference: dialogNormHideReference,
                    groupByNormRelative: (dialogNormGroupByRelative.length >= 1 && dialogNormGroupByRelative[0].value === 'dummy') ? undefined : dialogNormGroupByRelative.map(option => option.value),
                    normRelativeUnits: dialogNormRelativeUnits,
                    pathwayMode: dialogPathwayMode,
                    pathwayScore: dialogPathwayScore
                };
                if (dialogDataPipelines[0]){
                    setDataPipeline(dialogDataPipelines[0])
                }
                setItem(currentControl.label, itemIndex, updatedItem);
            }
        }
        setModalOpen(false);
    };

    useEffect(() => {
        for (let group of controlGroups) {
            for (let control of group.controls) {
                if (control.type === 'axis controls' && control.disableAddRow && !selectedItems[control.label]) {
                    const valueObj: ExtendedOptionValue = { value: '', label: '' };
                    handleTableDropdownChange(control, valueObj);
                }
            }
        }

    }, [plotConfig]);


    
    const handleTableDropdownChange = (control: Control, valueObj: ExtendedOptionValue, rowIndex?: number) => {
        const controlLabel = control.label;
    
        if (rowIndex !== undefined) {
            const foundOption = control.options?.find(item => item.label === valueObj.value);
            var method = 'data'

            if ( sampleSelections.includes(valueObj.value)) {
                method = 'index'
            } 
        
            const updatedItem: AxisItem = {
                name: valueObj.value,
                method: method, 
                pipelines: dialogDataPipelines, 
                versionType: foundOption?.id? foundOption.id : null,
                values: [],
              };

            if (method === 'data') {
                handleEditItem(control, updatedItem)
            }

            setItem(controlLabel, rowIndex, updatedItem);
        } else {
            // Transform ExtendedOptionValue to AxisItem
            const newAxisItem = transformToAxisItem(valueObj);

            // Add new AxisItem
            addItem(controlLabel, [newAxisItem]);
        }
    };
    
    
    const flattenOptions = (options: (OptionValue | OptionGroup)[]): ExtendedOptionValue[] => {
        const flattened: ExtendedOptionValue[] = [];
        options.forEach(option => {
            if ('options' in option) {  // It's an OptionGroup
                option.options.forEach(subOption => {
                    flattened.push({ ...subOption, group: option.label });
                });
            } else {  // It's an OptionValue
                flattened.push(option);
            }
        });
        return flattened;
    };

    // Function to get the appropriate icon based on the item type
    const getTypeIcon = (method: string) => {
        if (method === 'index') {
            return <CategoryIcon fontSize="small" sx={{  mr: 1.5, color: 'grey', ml: 0.5 }} />;
        }

        else if (method === 'data') {
            return <DataArrayIcon fontSize="small" sx={{ mr: 1.5, color: 'grey', ml: 0.5 }} />;
        }

        return <DataObjectIcon fontSize="small" sx={{ mr: 1.5, color: 'grey', ml: 0.5 }} />;
    };

    // Function to render the value in the dropdown
    const renderDropdownValue = (item: AxisItem) => {
        // Determine the display value
        let displayValue = item.name;

        if (item.values.length === 1 && item.values[0] !== '') {
            // If there's only one child, append it to the parent item
            displayValue += ` - ${item.values[0]}`;
        } else if (item.values.length > 1) {
            // If there are multiple children, add a color pill
            displayValue += ' ';
            return (
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1}>{displayValue}</Box>
                    <ColorPill 
                        count={item.values.length}
                        color={getColorForValue(item.values.length)} 
                    />
                </Box>
            );
        }

        // Return the display value with the type icon
        return (
            <Box display="flex" alignItems="center">
                <Box flexGrow={1}>{displayValue}</Box>
            </Box>
        );
    };

    let filterItems = currentControl  ? currentControl.options : [];
    if (!filterItems) {
        filterItems = [];
    }

    let groupByOptions = categoricalColumns ? Object.keys(categoricalColumns).map((key: any) => ({label: key.replace('|', ' '), value: key})) : []
    let referenceColumnOptions = categoricalColumns ? Object.keys(categoricalColumns).map((key: any) => ({label: key.replace('|', ' '), value: key})) : []

    // let referenceValueOptions = !dialogReferenceColumn.length ? [] : categoricalColumns[dialogReferenceColumn[0].value].map((value: any) => ({label: value as string, value: value as string}))

    let normByOptions: SearchableDropdownOption[] = []
    normByOptions = (currentControl && getSubOptions(currentControl, currentItem?.name || "") || []).map(option => ({label: option.value, value: currentItem?.name + '|' + option.value, pipeline: option?.pipeline}))
    // Object.keys(availableColumns).forEach((key: string) => {
    //     let value = availableColumns[key]
    //     for (let col of value){
    //         let newValue = key
    //         if (col !== ''){
    //             newValue += '|' + col
    //         }
    //         if (categoricalColumns[newValue] === undefined) {
    //             normByOptions.push({label:col, value:newValue})
    //         }
    //     }
    // })

    if (showGrouped) {
        groupByOptions = [{label: 'Sample Group', value: 'Sample Group'}, ...groupByOptions]
    }

    return (
        <Box sx={{ width: '100%', padding: '.5rem', overflowY: 'scroll', paddingRight: 2, overflowX: 'hidden'}}>
            <Divider />

            {/* <CustomFilterModal
                open={customFilterModalOpen}
                onClose={() => setCustomFilterModalOpen(false)}
                onSave={() => {handleFilterDialogSave(); setCustomFilterModalOpen(false); updateFilters()}}
                setFilterValues={handleFilterValue}
                categoricalColumns={categoricalColumns}
                filterValue={filterValue}
            /> */}
            
            <Dialog 
                open={modalOpen} 
                onClose={() => { setModalOpen(false); }}
                maxWidth="sm"  
                fullWidth      
            >
                <DialogTitle>
                    Data Pipeline
                </DialogTitle>
                <DialogContent>
                    <Box>
                        {/* Checkbox for "Treat as Category" */}
                        {/* <ListItem sx={{ justifyContent: 'flex-start', paddingY: 0 }}> */}
                        {/* <ListItemText sx={{ my: 0 }} secondary="Data Selection:" /> */}
                        <Accordion
                            key={"norm"}
                            elevation={0}
                            expanded={dataSelectExpand}
                            onChange={() => {
                              setDataSelectExpand(!dataSelectExpand) 
                            }}
                            sx={{
                                border: '1px solid lightgray',
                                // borderBottom: 0,
                                '&:before': { display: 'none' },
                                margin: 0,
                            }}
                        >   
                                <CustomAccordionSummary
                                    expandIcon={<ExpandMore />}
                                    aria-controls={`panel-norm-content`}
                                    id={`panel-norm-header`}
                                    // sx={{ minHeight:'30px' }}
                                >
                                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                        <b>Select</b>
                                    </Typography>
                                </CustomAccordionSummary >
                            <AccordionDetails>
                                <Box sx={{width:'100%', p:1}}>
                                    <SearchableDropdown
                                        multiple={!treatAsCategory}
                                        fullWidth
                                        label="Select Column"
                                        value={dialogSelectedColumn}
                                        setValue={setDialogSelectedColumn}
                                        options={(currentControl && getSubOptions(currentControl, currentItem?.name || "") || []).map(option => ({label: option.value, value: option.value, pipeline: option?.pipeline}))}
                                        placeholder="Data Selection"
                                    />
                                </Box>
                                <Box sx={{width:'100%', p:1}}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={treatAsCategory}
                                                onChange={handleCategoryToggleChange}
                                                name="categoricalToggle"
                                                color="primary"
                                                size="small" 
                                            />
                                        }
                                        label="Categorical"
                                        labelPlacement='start'
                                        sx={{
                                            marginTop: 2,
                                            marginLeft: 1,
                                            '& .MuiFormControlLabel-label': {
                                                marginRight: 2,
                                            },
                                        }}
                                    />
                                </Box>

                            </AccordionDetails>
                        </Accordion>

                        {/* <ListItem style={{ width: '100%' }}> */}
                            
                        {/* </ListItem> */}
                        
                        {currentItem?.method === 'data' && <DataProcessingStep groupBy={dialogGroupBy.map(key => (key.value))} setGroupBy={setDialogGroupBy}
                                            groupbyOptions={groupByOptions} aggregationMethod={dialogAggregationMethod} setAggregationMethod={setDialogAggregationMethod}
                                            normalizeBy={dialogNormalizeBy} normByOptions={normByOptions} setNormalizeBy={setDialogNormalizeBy} normalizeUnits={dialogNormalizeUnits} setNormalizeUnits={setDialogNormalizeUnits}
                                            relativeUnits={dialogRelativeUnits} setRelativeUnits={setDialogRelativeUnits} references={dialogReferences} 
                                            referenceColumnOptions={referenceColumnOptions} setReferences={setDialogReferences} 
                                            referenceValueOptions={categoricalColumns} processingMode={dialogProcessingMode} setProcessingMode={setDialogProcessingMode}
                                            hideReference={dialogHideReference} setHideReference={setDialogHideReference}
                                            groupByRelative={dialogGroupByRelative?.map(key => (key.value))} setGroupByRelative={setDialogGroupByRelative}
                                            normReferences={dialogNormReferences} setNormReferences={setDialogNormReferences} normHideReference={dialogNormHideReference} setNormHideReference={setDialogNormHideReference}
                                            groupByNormRelative={dialogNormGroupByRelative?.map(key => (key.value))} setGroupByNormRelative={setDialogNormGroupByRelative}
                                            normRelativeUnits={dialogNormRelativeUnits} setNormRelativeUnits={setDialogNormRelativeUnits}
                                            pathwayMode={dialogPathwayMode} setPathwayMode={setDialogPathwayMode} pathwayScore={dialogPathwayScore} setPathwayScore={setDialogPathwayScore}
                                            showPathwayMode={(currentItem !== null && (currentItem.versionType === 3 || currentItem.versionType === 192) )}
                                            error={error} setError={setError}
                                            />}

                        
                        {/* <CustomFilterStep
                            setFilterValues={handleFilterValue}
                            categoricalColumns={categoricalColumns}
                            filterValue={filterValue}
                        /> */}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={handleDialogSave} disabled={error !== ''}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
    
            {controlGroups.map((group, index) => {
                return (
                <div key={index}>
                    
                    {group.controls.filter(item => item.label).map((control) => {

                        let axisItems = (selectedItems[control.label] || []).filter(item => Object.keys(availableColumns).includes(item.name) || !item.name)
                        
                        if (control.type === 'axis controls' && control.options) {
                            let allMethods = selectedItems[control.label]?.map(item => item.method)
                            let disableAddTemporary = false;
                             
                            // if (allMethods && allMethods.includes('categorical')) {
                            //     disableAddTemporary = true;
                            // }
                            
                            return (    
                                <Box key={group.title} sx={{ margin: '2rem 0' }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1, marginLeft: 2 }}>
                                        <Typography variant="subtitle1"><b><u>{group.title}</u></b></Typography>
                                        {!control.disableAddRow && <Button 
                                            size="small"
                                            onClick={() => {
                                                group.controls.forEach(control => clearItems(control.label));
                                            }}
                                        >
                                            Clear All
                                        </Button>}
                                    </Box>
                                    <Divider />
                                    <TableContainer component={Box} sx={{ width: '100%', maxWidth: '100%' }} key={control.label}>
                                        <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', paddingRight: 2 }}>
                                            <colgroup>
                                                <col style={{ width: '79%' }} />
                                                <col style={{ width: '21%' }} />
                                            </colgroup>
                                        <TableBody>
                                            {axisItems.map((item, index) => {
                                                    return (
                                                    <TableRow key={`${control.label}-${index}`}>
                                                        <TableCell sx={{marginLeft: 0, paddingLeft: 1}}>
                                                        <Box display="flex" alignItems="center">
                                                             {getTypeIcon(item.method)}
                                                            <Select
                                                                value={item.name in availableColumns ? item.name : ''}
                                                                fullWidth
                                                                displayEmpty
                                                                sx={{ 
                                                                    height: '40px', 
                                                                    padding: '0',
                                                                }}
                                                                onChange={(e) => {
                                                                    const valueObj: ExtendedOptionValue = { value: e.target.value, label: e.target.value, id: item.versionType? item.versionType : undefined};
                                                                    handleTableDropdownChange(control, valueObj, index);
                                                                }}
                                                                renderValue={() => renderDropdownValue(item)}
                                                            >
                                                            
                                                                {/* Sample Selections Group */}
                                            
                                                                {!control.disableSubheaders && <ListSubheader>Sample Selections</ListSubheader>}
                                                                {sampleSelections.map(optionLabel => 
                                                                    control.options?.some(option => option.label === optionLabel) && (
                                                                        <MenuItem key={optionLabel} value={optionLabel}>
                                                                            {optionLabel}
                                                                        </MenuItem>
                                                                    )
                                                                )}
    
                                                                {/* Analysis Types Group */}
                                                                {!control.disableSubheaders && <ListSubheader>Analysis Types</ListSubheader>}
                                                                {control.options?.map(option => 
                                                                    !sampleSelections.includes(option.label) && (
                                                                        <MenuItem key={option.label} value={option.label}>
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                            </Select>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{paddingLeft: 0}}>
                                                            <Box display="flex" alignItems="center" justifyContent='flex-start'>
                                                                
                                                                {/* {item.method === 'data' && 
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ padding: 0}}
                                                                        onClick={() => handleFilterItem(control, item)}
                                                                    >
                                                                        <FilterAltIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                } */}
                                                                {item.method !== 'index' && <IconButton
                                                                    size="small"
                                                                    sx={{ padding: 0, marginLeft: '4px' }}
                                                                    onClick={() => handleEditItem(control, item)}
                                                                    >
                                                                        <SettingsIcon fontSize="inherit" />
                                                                
                                                                    </IconButton>
                                                                } 
                                                                {!control.disableAddRow && (
                                                                <IconButton 
                                                                    size="small" 
                                                                    sx={{ padding: 0, marginLeft: '4px' }}
                                                                    onClick={() => removeItem(control.label, index)}
                                                                >
                                                                    <DeleteIcon fontSize="inherit" />
                                                                </IconButton>
                                                                )}
                                                            </Box>
                                                            </TableCell>
                                                    </TableRow>
                                            )})}
                                            {(!control.disableAddRow) && canAddRow(control.label) && (
                                                <TableRow>
                                                    <TableCell colSpan={3}>
                                                        <Button 
                                                            disabled={disableAddTemporary}
                                                            onClick={() => {
                                                            const valueObj: ExtendedOptionValue = { value: '', label: '' };
                                                            handleTableDropdownChange(control, valueObj);
                                                        }}>
                                                            + Add Row
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            );
                        }
                        
                    else if (control.type === 'dropdown' && control.options) {
                        return (
                            <Box key={group.title} sx={{ margin: '2rem 0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2, justifyContent: 'space-between', paddingLeft: '15px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ flexGrow: 0, marginRight: '8px' }}><b><u>{group.title}</u></b></Typography>
                            <IconButton size="small"
                                onClick={() => {
                               
                                    openPlotOptionsModal(); 
                                }}
                            >
                                <SettingsIcon fontSize="inherit" />
                            </IconButton>
                            </Box>

                            <Box>
                            <Button 
                                variant="outlined" 
                                size='small' 
                                disabled={disableSave}
                                sx={{
                                    color: saveSuccess ? 'white' : 'grey', 
                                    border: saveSuccess ? '1px solid green' : '1px solid grey',
                                    bgcolor: saveSuccess ? 'green' : 'inherit', // Change background color to green if saveSuccess is true
                                    '&:hover': {
                                    bgcolor: saveSuccess ? 'darkgreen' : '#f8f9fa', // Darken the color slightly on hover
                                    border: saveSuccess ? 'darkgreen' : '1px solid grey', // Also darken the border color on hover
                                    },
                                }} 
                                onClick={() => {
                                    savePlot(false)
                                }}
                                >
                                {saveSuccess ? 'Saved' : 'Save'} 
                                </Button>

                            <Button variant="outlined" size='small' color='inherit' sx={{'color': 'grey', 'border': '1px solid grey', 'marginLeft': '5px'}} onClick={() => {
                                savePlot(true); 
                            }}>
                                Save As
                            </Button>
                            </Box>
                                </Box>
                                <Divider sx={{marginBottom: '10px'}} />
                                {/* <FormControl fullWidth variant="standard" sx={{ margin: '1rem 15px' }}>
                                    <TextField
                                    size="small"
                                    label={control.textFieldLabel}
                                    variant="outlined"
                                    value={plotName}
                                    onChange={(e) => handlePlotNameChange(e.target.value as string)}
                                    fullWidth
                                    />
                                </FormControl> */}
                            
                                <FormControl fullWidth variant="standard" sx={{ margin: '1rem 15px' }}>
                                    <InputLabel id={`${control.label}-label`}>{control.label}</InputLabel>
                                <Select
                                    labelId={`${control.label}-label`}
                                    id={control.label}
                                    label={control.label}
                                    value={control.options.length > 0 ? (plotConfig['plotType'] || control.defaultValue || "") : ''}
                                    onChange={(e) => handlePlotConfigChange('plotType', e.target.value as string)}
                                >
                                    {control.options?.map(option => {
                                    if ('options' in option) { // This is an OptionGroup
                                        return (
                                            <div key={option.label}>
                                                <ListSubheader sx={{ fontSize: '1rem', margin: '0.5rem 0' }}>{option.label}</ListSubheader>
                                                {option.options.map(subOption => (
                                                    <MenuItem key={subOption.value} value={subOption.value} sx={{ fontSize: '0.8rem' }}>
                                                        {subOption.label}
                                                    </MenuItem>
                                                ))}
                                            </div>
                                        );
                                    } else { // This is an OptionValue
                                        return (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        );
                                    }
                                })}
                                </Select>
                            </FormControl>
                            </Box>
                        );
                    } else if (control.type === 'slider') {
                        return (
                            <Box key={group.title} sx={{ margin: '2rem 0' }}>
                            <Typography variant="subtitle1" sx={{ marginBottom: 2, marginLeft: 2 }}><b><u>{group.title}</u></b></Typography>
                            <Divider />
                            <FormControl fullWidth variant="standard" key={control.label} sx={{ marginLeft: 2 }}>
                                <Typography gutterBottom>{control.label}</Typography>
                                <Slider
                                    defaultValue={Number(control.defaultValue) || Number(control.min)}
                                    step={control.step}
                                    marks
                                    min={control.min}
                                    max={control.max}
                                    valueLabelDisplay="auto"
                                />
                            </FormControl>
                            </Box>
                        );
                    } else if (control.type === 'multiselect' && control.options) {
                        const values = (selectedItems[control.label] || []).map(item => item.name);
                        return (
                            <Box key={group.title} sx={{ margin: '2rem 0' }}>
                            <Typography variant="subtitle1" sx={{ marginBottom: 2, marginLeft: 2 }}><b><u>{group.title}</u></b></Typography>
                            <Divider />
                            <FormControl fullWidth variant="standard" sx={{ marginTop: 2, marginLeft: 2 }} key={control.label}>
                                <InputLabel>{control.label}</InputLabel>
                                <Select
                                    multiple
                                    value={values}
                                    onChange={(e) => {
                                        const selectedValues = e.target.value as string[];
                                        const selectedOptions = selectedValues
                                            .map(val => flattenOptions(control.options || [])
                                            .find(option => option.value === val))
                                            .filter(Boolean) as ExtendedOptionValue[];
                                        
                                        selectedOptions.forEach(option => {
                                            handleTableDropdownChange(control, option);
                                        });
                                    }}
                                    renderValue={(selected: any) => selected.join(', ')}
                                >
                                    {control.options?.map(option => {
                                        if ('options' in option) {
                                            return option.options.map(subOption => (
                                                <MenuItem key={subOption.value} value={subOption.value}>
                                                    <Checkbox checked={values.includes(subOption.value)} />
                                                    <ListItemText primary={subOption.label} />
                                                </MenuItem>
                                            ));
                                        } else {
                                            return (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Checkbox checked={values.includes(option.value)} />
                                                    <ListItemText primary={option.label} />
                                                </MenuItem>
                                            );
                                        }
                                    })}
                                </Select>
                            </FormControl>
                            </Box>
                        );
                    }
                    
                    return null; 

                    })}
                </div>)}

            )}
        </Box>
    );
};

export default PlotControls;
